(() => {
  'use strict';

  const STORAGE_KEY = 'tf:wishlist:v1';
  const MAX_STORED_PRODUCTS = 100;
  const CHANGE_EVENT = 'wishlist:change';

  function normalizeHandle(value) {
    const handle = String(value || '')
      .trim()
      .toLowerCase();

    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)
      ? handle
      : '';
  }

  function normalizeItems(items) {
    if (!Array.isArray(items)) return [];

    return Array.from(
      new Set(
        items
          .map(normalizeHandle)
          .filter(Boolean)
      )
    ).slice(0, MAX_STORED_PRODUCTS);
  }

  function arraysEqual(first, second) {
    if (first.length !== second.length) return false;

    return first.every((item, index) => item === second[index]);
  }

  function getWishlistAnnouncer() {
    let announcer = document.querySelector(
      '[data-wishlist-live-region]'
    );

    if (announcer) return announcer;

    announcer = document.createElement('span');
    announcer.className = 'visually-hidden';
    announcer.setAttribute('data-wishlist-live-region', '');
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');

    document.body.appendChild(announcer);

    return announcer;
  }

  function announce(message) {
    if (!message) return;

    const announcer = getWishlistAnnouncer();

    announcer.textContent = '';

    window.requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  function syncWishlistCounts(count) {
    document
      .querySelectorAll('[data-wishlist-count]')
      .forEach((element) => {
        element.textContent = String(count);
        element.toggleAttribute('hidden', count === 0);
      });
  }

  class WishlistStore {
    constructor() {
      this.listeners = new Set();
      this.items = this.read();

      this.handleStorageChange =
        this.handleStorageChange.bind(this);

      window.addEventListener(
        'storage',
        this.handleStorageChange
      );
    }

    read() {
      try {
        const value = window.localStorage.getItem(STORAGE_KEY);

        if (!value) return [];

        return normalizeItems(JSON.parse(value));
      } catch (error) {
        console.warn(
          '[Wishlist] Unable to read local storage.',
          error
        );

        return [];
      }
    }

    write() {
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(this.items)
        );

        return true;
      } catch (error) {
        /*
         * Keep the in-memory wishlist working even when storage
         * is blocked or unavailable.
         */
        console.warn(
          '[Wishlist] Unable to save local storage.',
          error
        );

        return false;
      }
    }

    getAll() {
      return [...this.items];
    }

    has(handle) {
      const normalizedHandle = normalizeHandle(handle);

      return normalizedHandle
        ? this.items.includes(normalizedHandle)
        : false;
    }

    subscribe(callback) {
      if (typeof callback !== 'function') {
        return () => {};
      }

      this.listeners.add(callback);

      return () => {
        this.listeners.delete(callback);
      };
    }

    emit(detail = {}) {
      const payload = {
        action: detail.action || 'change',
        handle: detail.handle || '',
        source: detail.source || null,
        items: this.getAll(),
        count: this.items.length
      };

      this.listeners.forEach((listener) => {
        try {
          listener(payload);
        } catch (error) {
          console.error(
            '[Wishlist] Listener failed.',
            error
          );
        }
      });

      document.dispatchEvent(
        new CustomEvent(CHANGE_EVENT, {
          detail: payload
        })
      );

      syncWishlistCounts(payload.count);
    }

    add(handle, source = null) {
      const normalizedHandle = normalizeHandle(handle);

      if (!normalizedHandle) {
        return {
          action: 'invalid',
          handle: ''
        };
      }

      if (this.has(normalizedHandle)) {
        return {
          action: 'existing',
          handle: normalizedHandle
        };
      }

      this.items = [
        normalizedHandle,
        ...this.items
      ].slice(0, MAX_STORED_PRODUCTS);

      this.write();

      this.emit({
        action: 'add',
        handle: normalizedHandle,
        source
      });

      return {
        action: 'add',
        handle: normalizedHandle
      };
    }

    remove(handle, source = null) {
      const normalizedHandle = normalizeHandle(handle);

      if (!normalizedHandle) {
        return {
          action: 'invalid',
          handle: ''
        };
      }

      const nextItems = this.items.filter(
        (item) => item !== normalizedHandle
      );

      if (arraysEqual(nextItems, this.items)) {
        return {
          action: 'missing',
          handle: normalizedHandle
        };
      }

      this.items = nextItems;
      this.write();

      this.emit({
        action: 'remove',
        handle: normalizedHandle,
        source
      });

      return {
        action: 'remove',
        handle: normalizedHandle
      };
    }

    toggle(handle, source = null) {
      return this.has(handle)
        ? this.remove(handle, source)
        : this.add(handle, source);
    }

    replace(items, action = 'replace', source = null) {
      const nextItems = normalizeItems(items);

      if (arraysEqual(nextItems, this.items)) {
        return;
      }

      this.items = nextItems;
      this.write();

      this.emit({
        action,
        source
      });
    }

    clear(source = null) {
      if (!this.items.length) return;

      this.items = [];
      this.write();

      this.emit({
        action: 'clear',
        source
      });
    }

    handleStorageChange(event) {
      if (event.key !== STORAGE_KEY) return;

      const nextItems = this.read();

      if (arraysEqual(nextItems, this.items)) return;

      this.items = nextItems;

      this.emit({
        action: 'storage'
      });
    }
  }

  const wishlistStore = new WishlistStore();

  class WishlistToggle extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.handleClick.bind(this);
      this.handleStoreChange =
        this.handleStoreChange.bind(this);

      this.unsubscribeStore = null;
    }

    connectedCallback() {
      this.button = this.querySelector(
        '[data-wishlist-button]'
      );

      this.tooltip = this.querySelector(
        '[data-wishlist-tooltip]'
      );

      this.productHandle = normalizeHandle(
        this.dataset.productHandle
      );

      if (!this.button || !this.productHandle) return;

      this.button.removeEventListener(
        'click',
        this.handleClick
      );

      this.button.addEventListener(
        'click',
        this.handleClick
      );

      this.unsubscribeStore?.();

      this.unsubscribeStore = wishlistStore.subscribe(
        this.handleStoreChange
      );

      this.render();
    }

    disconnectedCallback() {
      this.button?.removeEventListener(
        'click',
        this.handleClick
      );

      this.unsubscribeStore?.();
      this.unsubscribeStore = null;
    }

    handleClick(event) {
      event.preventDefault();

      const result = wishlistStore.toggle(
        this.productHandle,
        this
      );

      if (result.action === 'add') {
        announce(this.dataset.addedMessage);
      }

      if (result.action === 'remove') {
        announce(this.dataset.removedMessage);
      }
    }

    handleStoreChange() {
      this.render();
    }

    render() {
      if (!this.button || !this.productHandle) return;

      const isActive = wishlistStore.has(
        this.productHandle
      );

      const label = isActive
        ? this.dataset.removeLabel
        : this.dataset.addLabel;

      const tooltip = isActive
        ? this.dataset.removeTooltip
        : this.dataset.addTooltip;

      this.button.setAttribute(
        'aria-pressed',
        isActive ? 'true' : 'false'
      );

      this.button.classList.toggle(
        'is-active',
        isActive
      );

      this.classList.toggle(
        'is-active',
        isActive
      );

      if (label) {
        this.button.setAttribute(
          'aria-label',
          label
        );
      }

      if (this.tooltip && tooltip) {
        this.tooltip.textContent = tooltip;
      }
    }
  }

  async function mapWithConcurrency(
    items,
    concurrency,
    callback
  ) {
    const results = new Array(items.length);
    let currentIndex = 0;

    const workerCount = Math.min(
      Math.max(concurrency, 1),
      items.length
    );

    const workers = Array.from(
      { length: workerCount },
      async () => {
        while (currentIndex < items.length) {
          const index = currentIndex;
          currentIndex += 1;

          try {
            results[index] = await callback(
              items[index],
              index
            );
          } catch (error) {
            results[index] = {
              handle: items[index],
              error
            };
          }
        }
      }
    );

    await Promise.all(workers);

    return results;
  }


  if (!customElements.get('wishlist-toggle')) {
    customElements.define(
      'wishlist-toggle',
      WishlistToggle
    );
  }

  function initializeWishlistCount() {
    syncWishlistCounts(
      wishlistStore.getAll().length
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeWishlistCount,
      {
        once: true
      }
    );
  } else {
    initializeWishlistCount();
  }

  window.TFWishlist = Object.freeze({
    getAll: () => wishlistStore.getAll(),

    has: (handle) => wishlistStore.has(handle),

    add: (handle, source = null) =>
      wishlistStore.add(handle, source),

    remove: (handle, source = null) =>
      wishlistStore.remove(handle, source),

    replace: (
      items,
      action = 'replace',
      source = null
    ) =>
      wishlistStore.replace(
        items,
        action,
        source
      ),

    clear: (source = null) =>
      wishlistStore.clear(source),

    subscribe: (callback) =>
      wishlistStore.subscribe(callback)
  });

  document.dispatchEvent(
    new CustomEvent('wishlist:ready')
  );
})();